import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { SelectComponent } from 'app/shared/components/select/select.component';
import { NumericOnlyDirective } from 'app/shared/directives/numeric-only.directive';
import {
  GetListSurgeryModel,
  MedicationCategory,
} from 'app/core/models/patient/patient.model';
import { AllergyCategory } from 'app/core/services/allergy/allergy.service';
import { ChronicCondition } from 'app/core/models/chronic-condition/chronic-condition.model';
import { ImmunizationStatus } from 'app/core/models/immunization-status/immunization-status.model';
import { FamilyMedicalHistory } from 'app/core/models/family-medical-history/family-medical-history.model';

@Component({
  selector: 'app-patient-medical-info-step',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    NgSelectModule,
    SelectComponent,
    NumericOnlyDirective,
  ],
  templateUrl: './patient-medical-info-step.component.html',
  styleUrl: './patient-medical-info-step.component.scss',
})
export class PatientMedicalInfoStepComponent implements OnInit {
  @Input() patientForm!: FormGroup;
  @Input() currentStep: number = 2;
  @Input() livingConditionsOptions: Array<{ id: number; name: string }> = [];
  @Input() skinTypeOptions: Array<{
    id: number;
    name: string;
    color: string;
    textColor: string;
  }> = [];
  @Input() dietTypeOptions: Array<{ id: number; name: string }> = [];
  @Input() bloodTypeOptions: Array<{ id: number; name: string }> = [];
  @Input() alcoholConsumptionOptions: Array<{ id: number; name: string }> = [];
  @Input() smokingStatusOptions: Array<{ id: number; name: string }> = [];
  @Input() allergyCategories: AllergyCategory[] = [];
  @Input() medications: MedicationCategory[] = [];
  @Input() surgeries: GetListSurgeryModel[] = [];
  @Input() immunizationStatus: ImmunizationStatus[] = [];
  @Input() familyMedicalHistory: FamilyMedicalHistory[] = [];
  @Input() chronicConditionArray: ChronicCondition[] = [];
  @Input() results: ChronicCondition[] = [];
  @Input() showDropdown: boolean = false;
  @Input() selectedAllergies: string[] = [];
  @Input() selectedMedications: string[] = [];
  @Input() selectedSurgeries: string[] = [];
  @Input() selectedWoundCareProducts: string[] = [];
  @Input() selectedAntibiotics: string[] = [];
  @Input() selectedDebridements: string[] = [];
  @Input() selectedFamilyHistory: string[] = [];
  @Input() selectedImmunizations: string[] = [];
  @Input() selectedVacType: number | null = null;
  @Input() selectedVacSubOptions: { [optionId: string]: string } = {};
  @Input() vacTouched: boolean = false;
  @Input() accordionState: any = {};

  @Output() toggleAllergy = new EventEmitter<string>();
  @Output() toggleMedication = new EventEmitter<string>();
  @Output() toggleSurgery = new EventEmitter<string>();
  @Output() toggleWoundCareProduct = new EventEmitter<string>();
  @Output() toggleAntibiotic = new EventEmitter<string>();
  @Output() toggleDebridement = new EventEmitter<string>();
  @Output() toggleFamilyHistory = new EventEmitter<string>();
  @Output() toggleImmunization = new EventEmitter<string>();
  @Output() toggleAccordion = new EventEmitter<string>();
  @Output() selectChronicCondition = new EventEmitter<ChronicCondition>();
  @Output() removeChronicCondition = new EventEmitter<number>();
  @Output() vacTypeChange = new EventEmitter<{ vacId: string; type: number }>();
  @Output() vacSubOptionChange = new EventEmitter<{
    optionId: string;
    answerId: string;
  }>();
  @Output() previousStep = new EventEmitter<void>();
  @Output() savePatient = new EventEmitter<void>();
  @Output() searchTermChange = new EventEmitter<string>();

  ngOnInit(): void {}

  onToggleAllergy(allergyId: string): void {
    this.toggleAllergy.emit(allergyId);
  }

  onToggleMedication(medicationId: string): void {
    this.toggleMedication.emit(medicationId);
  }

  onToggleSurgery(surgeryId: string): void {
    this.toggleSurgery.emit(surgeryId);
  }

  onToggleWoundCareProduct(productId: string): void {
    this.toggleWoundCareProduct.emit(productId);
  }

  onToggleAntibiotic(antibioticId: string): void {
    this.toggleAntibiotic.emit(antibioticId);
  }

  onToggleDebridement(debridementId: string): void {
    this.toggleDebridement.emit(debridementId);
  }

  onToggleFamilyHistory(id: string): void {
    this.toggleFamilyHistory.emit(id);
  }

  onToggleImmunization(id: string): void {
    this.toggleImmunization.emit(id);
  }

  onToggleAccordion(section: string): void {
    this.toggleAccordion.emit(section);
  }

  onSelectChronicCondition(item: ChronicCondition): void {
    this.selectChronicCondition.emit(item);
  }

  onRemoveChronicCondition(index: number): void {
    this.removeChronicCondition.emit(index);
  }

  onVacTypeChange(vacId: string, type: number): void {
    this.vacTypeChange.emit({ vacId, type });
  }

  onVacSubOptionChange(optionId: string, answerId: string): void {
    this.vacSubOptionChange.emit({ optionId, answerId });
  }

  isAllergySelected(allergyId: string): boolean {
    return this.selectedAllergies.includes(allergyId);
  }

  isMedicationSelected(medicationId: string): boolean {
    return this.selectedMedications.includes(medicationId);
  }

  isSurgerySelected(surgeryId: string): boolean {
    return this.selectedSurgeries.includes(surgeryId);
  }

  isWoundCareProductSelected(productId: string): boolean {
    return this.selectedWoundCareProducts.includes(productId);
  }

  isAntibioticSelected(antibioticId: string): boolean {
    return this.selectedAntibiotics.includes(antibioticId);
  }

  isDebridementSelected(debridementId: string): boolean {
    return this.selectedDebridements.includes(debridementId);
  }

  isFamilyHistorySelected(id: string): boolean {
    return this.selectedFamilyHistory.includes(id);
  }

  isImmunizationSelected(id: string): boolean {
    return this.selectedImmunizations.includes(id);
  }

  isVacSubOptionSelected(optionId: string, answerId: string): boolean {
    return this.selectedVacSubOptions[optionId] === answerId;
  }

  // getSpongeOptions(): VacuumOptionDto[] {
  //   const spongeType = this.vacList.find((v) => v.vacuumType === 1);
  //   return spongeType ? spongeType.vacuumOptionDtos : [];
  // }

  // isVacValid(): boolean {
  //   if (this.selectedVacType === null || this.selectedVacType === 0) {
  //     return true;
  //   }
  //   if (this.selectedVacType === 1) {
  //     const options = this.getSpongeOptions();
  //     return options.every((opt) => !!this.selectedVacSubOptions[opt.id]);
  //   }
  //   return true;
  // }

  trackByCode = (index: number, item: ChronicCondition) => item.code || item.id;

  onPreviousStep(): void {
    this.previousStep.emit();
  }

  onSavePatient(): void {
    this.savePatient.emit();
  }

  onSearchInput(event: any) {
    const value = event.target.value;
    this.searchTermChange.emit(value);
  }

  formatPhysicalInput(fieldName: string): void {
    const control = this.patientForm.get(`physicalLifestyle.${fieldName}`);
    if (control && control.value !== null && control.value !== '') {
      let value = control.value;

      if (typeof value === 'string') {
        value = value.replace(',', '.');
      }

      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        const formattedValue = parseFloat(numValue.toFixed(1));
        control.setValue(formattedValue, { emitEvent: false });
      }
    }
  }
}
